import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import {DropdownForm} from "../../base/dropdown/dropdown";
import {InputForm} from "../../base/input/input";
import Button from "../../base/button/button";
import Form from "../../base/form";
import {Add} from "@mui/icons-material";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {Paper, SvgIcon} from "@mui/material";
import {ReactComponent as Excel} from "../../../../assets/images/excel.svg";
import {SwitchForm} from "../../base/switch";
import {MuiRFF, Yup} from '../../form';
import DateUtils from "../../../../core/services/date-utils";
import {DatePickerForm} from "../../base/date-picker";
import useAccessLevels from "../../../hooks/use-access-levels";

const formNames = {
    createdDateStart: 'createdDateStart',
    createdDateEnd: 'createdDateEnd',
    expiryDateStart: 'expiryDateStart',
    expiryDateEnd: 'expiryDateEnd',
    category: 'category',
    keyword: 'keyword',
    minimum: 'minimum',
    maximum: 'maximum',
    active: 'active',
}

const schema = Yup.object().shape({
    [formNames.createdDateStart]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formNames.createdDateEnd), ValidateMessages.lessThan('Created Start Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    [formNames.createdDateEnd]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formNames.createdDateStart), ValidateMessages.greaterThan('Created End Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    [formNames.expiryDateStart]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formNames.expiryDateEnd), ValidateMessages.lessThan('Expiry Start Date'))
        .nullable(),
    [formNames.expiryDateEnd]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formNames.expiryDateStart), ValidateMessages.greaterThan('Expiry End Date'))
        .nullable(),
    [formNames.category]: Yup.mixed().nullable(),
    [formNames.keyword]: Yup.string().nullable().min(3, ValidateMessages.minLength('Keywords', 3)),
    [formNames.minimum]: Yup.number().nullable().typeError(ValidateMessages.incorrectType('Number')),
    [formNames.maximum]: Yup.number().nullable().typeError(ValidateMessages.incorrectType('Number')),
    [formNames.active]: Yup.boolean().nullable().default(false),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const GiftCardSearchSection = ({
                                   openCreateGiftCard,
                                   openImportFromExcel,
                                   giftCardCategories: categoriesProp,
                                   searchData,
                               }) => {
    const [categories, setCategories] = useState([]);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    /**
     * Fetches the list of gift card categories for the company of the operator. If the result of the api is successful,
     * sets the list.
     */
    const getCategories = useCallback(() => {
        Api.getGiftCardCategories().then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setCategories(response?.data ?? [])
            }
        })
    }, [isMounted])

    /**
     * Gets the list of gift card categories as soon as this component mounts.
     */
    useEffect(() => {
        if (!categoriesProp) {
            getCategories();
        }
    }, [getCategories])

    /**
     * Listens for the changes of categories prop and with each change, sets the state
     */
    useEffect(() => {
        if (!categoriesProp) return;
        setCategories(categoriesProp);
    }, [categoriesProp])


    /**
     * Changes the query of the url to the query containing the searched values so that the list pages of orders can
     * use them and search related orders.
     * @param values {any} form values
     */
    const onSubmit = (values) => {
        searchData.setFilters({
            minimum: values[formNames?.minimum],
            maximum: values[formNames?.maximum],
            createdDateStart: values[formNames.createdDateStart] ? DateUtils.UTCToServer(values[formNames.createdDateStart]).format(DateUtils.withoutOffsetFormat) : undefined,
            createdDateEnd: values[formNames.createdDateEnd] ? DateUtils.UTCToServer(values[formNames.createdDateEnd]).format(DateUtils.withoutOffsetFormat) : undefined,
            expiryDateStart: values[formNames.expiryDateStart] ? DateUtils.UTCToServer(values[formNames.expiryDateStart]).format(DateUtils.withoutOffsetFormat) : undefined,
            expiryDateEnd: values[formNames.expiryDateEnd] ? DateUtils.UTCToServer(values[formNames.expiryDateEnd]).format(DateUtils.withoutOffsetFormat) : undefined,
            category: values[formNames.category] ? JSON.stringify(values[formNames.category]) : undefined,
            active: values[formNames?.active],
            keyword: values[formNames.keyword],
        })
    }

    const initialValues = useMemo(() => ({
        ...(searchData.filters ?? {}),
        [formNames.category]: searchData.filters?.category ? categories.find(e => e.id === JSON.parse(searchData.filters.category)?.id) : null,
        [formNames.createdDateStart]: searchData.filters?.createdDateStart ? DateUtils.serverToUTC(searchData.filters?.createdDateStart) : undefined,
        [formNames.createdDateEnd]: searchData.filters?.createdDateEnd ? DateUtils.serverToUTC(searchData.filters?.createdDateEnd) : undefined,
        [formNames.expiryDateStart]: searchData.filters?.expiryDateStart ? DateUtils.serverToUTC(searchData.filters?.expiryDateStart) : undefined,
        [formNames.expiryDateEnd]: searchData.filters?.expiryDateEnd ? DateUtils.serverToUTC(searchData.filters?.expiryDateEnd) : undefined,
        [formNames.active]: typeof searchData.filters?.active === 'boolean' ? searchData.filters.active : true,
    }), [categories, searchData.filters]);

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({submitting, values, form}) => {
                const colClassName = 'px-2 mb-4';
                return (
                    <Row className='no-gutters align-items-center justify-content-center'>
                        <Col xs={12}
                             className={`${colClassName} row align-items-center justify-content-end np-gutters`}>
                            {
                                operatorAccessLevels.packages.sales.manageGiftCards &&
                                <Button
                                    className='d-flex align-items-center justify-content-center'
                                    variant={'outlined'}
                                    color={'primary'}
                                    onClick={() => openCreateGiftCard(true)}
                                >
                                    <Add className='icon small'/>
                                    <p className='ml-2'>Add new Gift Card / Coupon</p>
                                </Button>
                            }
                            {
                                operatorAccessLevels.packages.sales.manageGiftCards &&
                                <Button
                                    className='ml-3 d-flex justify-content-center align-items-center'
                                    variant={'outlined'}
                                    color={'primary'}
                                    onClick={() => openImportFromExcel(true)}
                                >
                                    <SvgIcon className='icon normal' component={Excel}/>
                                    <p className='ml-2'>Import from Excel</p>
                                </Button>
                            }
                        </Col>
                        <Col xs={12} lg={6} className={colClassName}>
                            <InputForm
                                value={values[formNames.keyword] ?? ''}
                                label={'Card Number'}
                                name={formNames.keyword}
                                required={required[formNames.keyword]}
                                onChange={(e) => form.change('keyword', e)}
                            />
                        </Col>
                        <Col xs={12} lg={6} className={colClassName}>
                            <DropdownForm
                                label={'Category'}
                                name={formNames.category}
                                required={false}
                                data={categories?.map(e => ({
                                    label: e.name,
                                    id: e.id,
                                    value: e,
                                })) ?? []}
                            />

                        </Col>
                        <Col xs={6} md={4} lg={5} className={colClassName}>
                            <InputForm
                                value={values[formNames.minimum] ?? ''}
                                label={'Min Amount'}
                                name={formNames.minimum}
                                required={required.minimum}
                                onChange={(e) => (!e && form.change('minimum', 0)) || (e && !isNaN(parseFloat(e)) && form.change('minimum', parseFloat(e)))}
                            />
                        </Col>
                        <Col xs={6} md={4} lg={5} className={colClassName}>
                            <InputForm
                                value={values[formNames.maximum] ?? ''}
                                label={'Max Amount'}
                                name={formNames.maximum}
                                required={required[formNames.maximum]}
                                onChange={(e) => (!e && form.change('maximum', 0)) || (e && !isNaN(parseFloat(e)) && form.change('maximum', parseFloat(e)))}
                            />
                        </Col>
                        <Col xs={6} md={4} lg={2}
                             className='px-2 mb-4 align-items-center row no-gutters justify-content-center'>
                            <Paper
                                elevation={0}
                                className={'py-2 w-100'}
                            >
                                <Row className={'no-gutters w-100 align-items-center justify-content-center mt-2 switch-form-container'}>
                                    <SwitchForm
                                        checked={values[formNames.active]}
                                        labelPlacement={'start'}
                                        label={'Active'}
                                        name={formNames.active}
                                        required={required[formNames.active]}
                                    />
                                </Row>
                            </Paper>
                        </Col>
                        <Col xs={12} md={6} className={colClassName}>
                            <DatePickerForm
                                name={formNames.createdDateStart}
                                label={'Created Start Date'}
                                required={required[formNames.createdDateStart]}
                                maxDate={values[formNames.createdDateEnd]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12} md={6} className={colClassName}>
                            <DatePickerForm
                                name={formNames.createdDateEnd}
                                label={'Created End Date'}
                                required={required[formNames.createdDateEnd]}
                                minDate={values[formNames.createdDateStart]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12} md={6} className={colClassName}>
                            <DatePickerForm
                                name={formNames.expiryDateStart}
                                label={'Expiry Start Date'}
                                required={required[formNames.expiryDateStart]}
                                maxDate={values[formNames.expiryDateEnd]}
                            />
                        </Col>
                        <Col xs={12} md={6} className={colClassName}>
                            <DatePickerForm
                                name={formNames.expiryDateEnd}
                                label={'Expiry End Date'}
                                required={required[formNames.expiryDateEnd]}
                                minDate={values[formNames.expiryDateStart]}
                            />
                        </Col>
                        <Col xs={12}
                             className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                            <Button
                                disabled={!!submitting}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='py-3 px-8'
                            >
                                Search
                            </Button>
                        </Col>

                    </Row>
                );
            }}
        />
    );
}

export default GiftCardSearchSection;
